<template>
  <ModalTemplate :modal="modal" :closable="!loading">
    <LoadingOverlay
      size="md"
      class="h-full rounded-lg md:rounded-xl"
      :active="loading"
      position-type="absolute"
      :delay="0"
    >
    </LoadingOverlay>
    <div class="pb-3 md:pb-5 md:pt-2">
      <template v-if="!success">
        <p class="mb-1 text-lg font-medium md:text-xl">
          {{ $t('control.Order') }}
        </p>
        <p class="mb-4 text-gray-500 dark:text-neutral-400">
          {{ $t('control.callback') }}
        </p>

        <form @submit.prevent="submitOrder">
          <div class="space-y-4">
            <div>
              <label
                for="name"
                class="mb-2 flex items-center gap-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                :class="{
                  'text-red-500 ': validation['name'],
                }"
              >
                {{ $t('order.Name') }}
                <div
                  v-if="validation['name']"
                  v-hint="{
                    position: 'bottom-center',
                    yOffset: 8,
                    text: $t(
                      'form.validation.errors.' + validation['name'][0].error,
                      validation['name'][0].params
                    ),
                    showDelay: 200,
                  }"
                >
                  <lucide-icon icon="CircleHelp" :size="14"></lucide-icon>
                </div>
              </label>
              <input
                id="name"
                v-model="form.name"
                autocomplete="section-form name"
                class="form-control h-10 w-full"
                :class="{
                  'invalid text-red-500': validation['name'],
                }"
                type="text"
                @input="clearValidationField('name')"
              />
            </div>

            <div>
              <label
                for="phone"
                class="mb-2 flex items-center gap-1 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                :class="{
                  'text-red-500 ': validation['phone_valid'],
                }"
              >
                {{ $t('order.Phone') }}
                <div
                  v-if="validation['phone_valid']"
                  v-hint="{
                    position: 'bottom-center',
                    yOffset: 8,
                    text: $t(
                      'form.validation.errors.' +
                        validation['phone_valid'][0].error,
                      validation['phone_valid'][0].params
                    ),
                    showDelay: 200,
                  }"
                >
                  <lucide-icon icon="CircleHelp" :size="14"></lucide-icon>
                </div>
              </label>
              <PhoneInput
                id="phone"
                v-model="form.phone"
                :auto-detect-country="true"
                :show-flag="true"
                :wrapper-class="`form-control !py-0 !px-0 has-focus-visible:!border-primary !pe-0 h-10 flex justify-between items-center w-full relative ${
                  validation['phone_valid'] ? 'invalid text-red-500 ' : ''
                }`"
                input-class="w-full h-10 py-1 px-3 transition-colors focus-visible:outline-none "
                flag-class="size-8 me-1 shrink-0 pointer-events-none"
                @onValidate="validatePhone"
                @input="clearValidationField('phone_valid')"
              />
            </div>

            <DynamicFields
              :fields="form.fields"
              :validation="validation"
              @clear-validation="clearValidationField"
              @update:fields="updateFields"
            />

            <button
              :inert="loading || animateShake"
              type="submit"
              :class="{
                'animate-shake': animateShake,
              }"
              class="shadow-primary/30 bg-primary text-onPrimary mt-2 flex h-10 w-full cursor-pointer items-center justify-center rounded-lg px-2 text-sm shadow-lg"
              :disabled="loading"
            >
              <lucide-icon icon="Send" class="me-2" size="14" />

              {{ $t('order.Submit') }}
            </button>
            <div class="leading-1">
              <a
                :href="Whitelabel.privacy_url"
                target="_blank"
                class="hover:text-primary text-xs text-gray-400 transition-colors dark:text-neutral-500"
              >
                {{ $t('order.Privacy') }}
              </a>
            </div>
          </div>
        </form>
      </template>
      <template v-else>
        <div class="flex flex-col items-center gap-2 md:gap-4">
          <lucide-icon
            icon="Check"
            class="mb-4 mt-4 text-green-500"
            :size="64"
          ></lucide-icon>

          <p class="text-center text-xl font-medium">
            {{ $t('titles.Send success') }}
          </p>
          <p
            class="mb-4 text-center text-sm text-gray-500 dark:text-neutral-400 md:mb-0 md:text-base"
          >
            {{ $t('titles.Success form') }}
          </p>

          <button
            class="btn h-9 bg-gray-100 px-4 py-2 hover:bg-gray-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
            @click="CLOSE_MODAL"
          >
            {{ $t('control.Close') }}
          </button>
        </div>
      </template>
    </div>
  </ModalTemplate>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import ModalTemplate from '@/components/modal/ModalTemplate.vue';
import PhoneInput from '@/components/ui/PhoneInput.vue';
import LoadingOverlay from '@/components/ui/LoadingOverlay';
import Hint from '@/libs/directives/Hint';

import { validateForm } from '@/utils/validationUtils.js';
import DynamicFields from '@/components/form/DynamicFields.vue';
export default {
  name: 'CallbackModal',
  components: {
    DynamicFields,
    ModalTemplate,
    PhoneInput,
    LoadingOverlay,
  },
  directives: {
    Hint,
  },
  props: {
    modal: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      phoneValid: false,
      animateShake: false,
      success: false,
      form: {
        name: null,
        phone: null,
        phone_valid: false,
        fields: [],
      },
      validation: [],
    };
  },
  computed: {
    ...mapGetters(['templates']),
  },
  beforeDestroy() {
    this.success = false;
    this.loading = false;
  },
  mounted() {
    this.form.fields = this.templates.modals.callback.fields.map((field) => ({
      ...field,
      value: null,
    }));
  },
  methods: {
    ...mapActions(['CLOSE_MODAL', 'OPEN_MODAL', 'axiosWrapped']),

    clearValidationField(fieldName) {
      if (this.validation[fieldName]) {
        const updatedValidation = { ...this.validation };
        delete updatedValidation[fieldName];
        this.validation = updatedValidation;
      }
    },
    updateFields(newFields) {
      this.form.fields = newFields;
    },

    validatePhone(e) {
      this.form.phone_valid = e.isValid;
    },

    async submitOrder() {
      const rules = [
        { name: 'name', rules: 'required|min:2|max:50' },
        { name: 'phone_valid', rules: 'required' },
        ...this.form.fields.map((field, index) => ({
          name: `fields[${index}].value`,
          rules: field.required ? 'required|max:240' : '',
        })),
      ];
      const validate = validateForm(this.form, rules);

      if (validate.valid == false) {
        this.validation = validate.errors.reduce((acc, error) => {
          acc[error.field] = error.errors;
          return acc;
        }, {});
        this.animateShake = true;
        setTimeout(() => {
          this.animateShake = false;
        }, 800);
        return;
      } else {
        this.validation = [];
      }

      this.loading = true;
      try {
        await this.axiosWrapped({
          url: `public/lead/`,
          method: 'post',
          body: {
            name: this.form.name,
            email: this.form.email,
            phone: this.form.phone,
            credit: this.form.credit,
            comment: this.form.comment,
            fields: this.form.fields,
          },
        });
        this.loading = false;
        if (this.$metrika) {
          this.$metrika.reachGoal('RPTcall');
        }
        if (this.$gtm) {
          this.$gtm.trackEvent({
            event: 'RPTcall',
          });
        }
        if (window.RPTAnalytics) {
          window.RPTAnalytics.event('orderCall');
        }
        this.$posthog.capture('orderCall');
        this.success = true;

        // this.response = response.data.data;
      } catch (error) {
        this.OPEN_MODAL({
          type: 'errorModal',
        });
        this.loading = false;
        this.success = false;
      }

      //   try {
      //     // Implement your API call here
      //     // const response = await this.axiosWrapped({
      //     //   url: 'public/order',
      //     //   params: this.form
      //     // });

      //     // Success handling
      //     this.$toast.success(this.$t('modals.order.success'));
      //     this.CLOSE_MODAL();
      //   } catch (error) {
      //     // Error handling
      //     this.$toast.error(this.$t('modals.order.error'));
      //     console.error('Order submission error:', error);
      //   } finally {
      //     this.loading = false;
      //   }
    },
  },
};
</script>
